import { useEffect, useState } from 'react'
import { useMiddlewareClient } from '@/hooks/useMiddlewareClient'
import { logError } from './errorUtils'

export interface CatalogTest {
  id: string
  code: string
  name: string
  category?: string
  categoryDisplay?: string
  specimen?: string
  isFavorite?: boolean
}

interface UseTestCatalogOptions {
  /** Free-text search (name or code) */
  q?: string
  /** Category code filter (e.g. "hematology") */
  category?: string
  /** Only return favorites */
  favoritesOnly?: boolean
  /** Max results (default 50) */
  _count?: number
  /** Skip fetch when false */
  enabled?: boolean
}

export function useTestCatalog(
  vendor: string,
  options?: UseTestCatalogOptions,
): { tests: CatalogTest[]; loading: boolean; total: number } {
  const middleware = useMiddlewareClient()
  const [tests, setTests] = useState<CatalogTest[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)

  const enabled = options?.enabled !== false
  const q = options?.q
  const category = options?.category
  const favoritesOnly = options?.favoritesOnly
  const count = options?._count

  useEffect(() => {
    if (!enabled) {
      setTests([])
      setTotal(0)
      setLoading(false)
      return
    }

    let mounted = true
    setLoading(true)

    middleware
      .get('/order/search-tests', {
        vendor,
        q: q?.trim() || undefined,
        category: category?.trim() || undefined,
        favoritesOnly: favoritesOnly || undefined,
        _count: count,
      })
      .then((data) => {
        if (!mounted) return
        setTests(data.tests)
        setTotal(data.total)
      })
      .catch((err) => {
        if (!mounted) return
        logError('Test catalog search', err)
        setTests([])
        setTotal(0)
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })

    return () => {
      mounted = false
    }
  }, [middleware, vendor, q, category, favoritesOnly, count, enabled])

  return { tests, loading, total }
}
